import { useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { FolderKanban, ListTodo, CalendarClock, FileText, Users } from 'lucide-react';
import Container from '@/components/ui/Container';
import DashboardHeader from '@/components/dashboard/DashboardHeader';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import FlatDashboardCard from '@/components/dashboard/FlatDashboardCard';
import { PLACEHOLDER_PROJECTS } from '@/hooks/placeholder-data/placeholderData';
import FulfillmentBoard from '@/components/fulfillment/FulfillmentBoard';
import { getEmptyColumns } from '@/data/fulfillmentData';
import TaskBoard from '@/components/tasks/TaskBoard';

const ProjectDetails = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const [activeTab, setActiveTab] = useState("kanban");
  
  // Find the project by ID from the placeholder data
  const project = PLACEHOLDER_PROJECTS.find(p => p.id === projectId);
  
  // If project not found, redirect to projects list
  if (!project) {
    return <Navigate to="/projects" replace />;
  }
  
  return (
    <Container>
      <DashboardHeader 
        title={project.name}
        description={project.description || "Manage and track this project's progress."}
        icon={<FolderKanban size={24} />}
      />
      
      <div className="mb-6">
        <div className="flex flex-col md:flex-row justify-between gap-4">
          <FlatDashboardCard className="flex-1 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-md">
                <CalendarClock size={20} className="text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Due Date</p>
                <p className="font-medium">{new Date(project.dueDate).toLocaleDateString()}</p>
              </div>
            </div>
          </FlatDashboardCard>
          
          <FlatDashboardCard className="flex-1 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-md">
                <Users size={20} className="text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Team Members</p>
                <p className="font-medium">3 members</p>
              </div>
            </div>
          </FlatDashboardCard>
          
          <FlatDashboardCard className="flex-1 p-4">
            <div className="flex items-center gap-3">
              <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-md">
                <ListTodo size={20} className="text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-xs text-muted-foreground font-medium">Progress</p>
                <p className="font-medium">{project.progress}% Complete</p>
              </div>
            </div>
          </FlatDashboardCard>
        </div>
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="w-full justify-start mb-6 bg-background/50 dark:bg-background/20 backdrop-blur-sm">
          <TabsTrigger value="kanban" className="flex gap-2">
            <FolderKanban size={16} />
            <span>Kanban</span>
          </TabsTrigger>
          <TabsTrigger value="tasks" className="flex gap-2">
            <ListTodo size={16} />
            <span>Tasks</span>
          </TabsTrigger>
          <TabsTrigger value="planning" className="flex gap-2">
            <CalendarClock size={16} />
            <span>Planning</span>
          </TabsTrigger>
          <TabsTrigger value="docs" className="flex gap-2">
            <FileText size={16} />
            <span>Documents</span>
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="kanban" className="mt-2">
          <FulfillmentBoard initialColumns={getEmptyColumns()} />
        </TabsContent>
        
        <TabsContent value="tasks" className="mt-2">
          <TaskBoard />
        </TabsContent>
        
        <TabsContent value="planning" className="mt-2">
          <FlatDashboardCard className="p-6">
            <h3 className="text-xl font-medium mb-4">Project Planning</h3>
            <p className="text-muted-foreground">
              Planning tools for this project will be displayed here.
            </p>
          </FlatDashboardCard>
        </TabsContent>
        
        <TabsContent value="docs" className="mt-2">
          <FlatDashboardCard className="p-6">
            <h3 className="text-xl font-medium mb-4">Project Documentation</h3>
            <p className="text-muted-foreground">
              Project documentation and files will be displayed here.
            </p>
          </FlatDashboardCard>
        </TabsContent>
      </Tabs>
    </Container>
  );
};

export default ProjectDetails; 